import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Calculator, IndianRupee, TrendingUp, Clock } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';

export default function EMICalculator() {
  const { lang } = useLanguage();
  const t = translations[lang].emi;
  const [plotSize, setPlotSize] = useState(1195);
  const [rate, setRate] = useState(3250);
  const [loanPercent, setLoanPercent] = useState(80);
  const [interestRate, setInterestRate] = useState(8.5);
  const [tenure, setTenure] = useState(20);

  const calculations = useMemo(() => {
    const totalCost = plotSize * rate;
    const loanAmount = (totalCost * loanPercent) / 100;
    const downPayment = totalCost - loanAmount;
    const monthlyRate = interestRate / 12 / 100;
    const months = tenure * 12;
    let emi = 0;
    if (monthlyRate > 0 && months > 0 && loanAmount > 0) {
      emi = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, months)) /
            (Math.pow(1 + monthlyRate, months) - 1);
    }
    const totalPayable = emi * months;
    const totalInterest = totalPayable - loanAmount;
    return { totalCost, loanAmount, downPayment, emi, totalPayable, totalInterest };
  }, [plotSize, rate, loanPercent, interestRate, tenure]);

  const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n));

  return (
    <section id="emi-calculator" data-testid="emi-calculator" className="py-16 sm:py-24 bg-[#F4F1EA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <span className="text-[#D48C57] text-xs font-medium tracking-[0.2em] uppercase">
            {t.sectionTag}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A2520] mt-3 tracking-tight">
            {t.title}
          </h2>
          <p className="text-[#5C6A61] text-base mt-3 max-w-2xl">{t.subtitle}</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Sliders */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5DFD3] space-y-6"
          >
            {/* Plot Size */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-medium text-[#5C6A61]">{t.plotSize}</label>
                <span className="text-sm font-bold text-[#1A2520]">{fmt(plotSize)} {t.sqft}</span>
              </div>
              <input
                type="range"
                min={200}
                max={3000}
                step={50}
                value={plotSize}
                onChange={(e) => setPlotSize(Number(e.target.value))}
                data-testid="emi-plot-size"
                className="w-full h-2 bg-[#E5DFD3] rounded-lg appearance-none cursor-pointer accent-[#2C4C3B]"
              />
              <div className="flex justify-between text-[10px] text-[#5C6A61] mt-1">
                <span>200</span>
                <span>3,000</span>
              </div>
            </div>

            {/* Rate */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-medium text-[#5C6A61]">{t.pricePerSqft}</label>
                <span className="text-sm font-bold text-[#1A2520]">{fmt(rate)}/{t.sqft}</span>
              </div>
              <input
                type="range"
                min={3000}
                max={3500}
                step={50}
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                data-testid="emi-rate"
                className="w-full h-2 bg-[#E5DFD3] rounded-lg appearance-none cursor-pointer accent-[#2C4C3B]"
              />
              <div className="flex justify-between text-[10px] text-[#5C6A61] mt-1">
                <span>3,000</span>
                <span>3,500</span>
              </div>
            </div>

            {/* Loan % */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-medium text-[#5C6A61]">{t.loanPercent}</label>
                <span className="text-sm font-bold text-[#1A2520]">{loanPercent}%</span>
              </div>
              <input
                type="range"
                min={50}
                max={90}
                step={5}
                value={loanPercent}
                onChange={(e) => setLoanPercent(Number(e.target.value))}
                data-testid="emi-loan-percent"
                className="w-full h-2 bg-[#E5DFD3] rounded-lg appearance-none cursor-pointer accent-[#2C4C3B]"
              />
            </div>

            {/* Interest Rate */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-medium text-[#5C6A61]">{t.interest}</label>
                <span className="text-sm font-bold text-[#1A2520]">{interestRate}% p.a.</span>
              </div>
              <input
                type="range"
                min={7}
                max={12}
                step={0.25}
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                data-testid="emi-interest"
                className="w-full h-2 bg-[#E5DFD3] rounded-lg appearance-none cursor-pointer accent-[#2C4C3B]"
              />
            </div>

            {/* Tenure */}
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-medium text-[#5C6A61]">{t.tenure}</label>
                <span className="text-sm font-bold text-[#1A2520]">{tenure} {t.years}</span>
              </div>
              <input
                type="range"
                min={5}
                max={30}
                step={1}
                value={tenure}
                onChange={(e) => setTenure(Number(e.target.value))}
                data-testid="emi-tenure"
                className="w-full h-2 bg-[#E5DFD3] rounded-lg appearance-none cursor-pointer accent-[#2C4C3B]"
              />
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-4"
          >
            {/* Monthly EMI - Hero card */}
            <div className="bg-[#2C4C3B] rounded-2xl p-6 sm:p-8 text-white" data-testid="emi-result">
              <div className="flex items-center gap-2 mb-1">
                <Calculator className="w-4 h-4 text-[#D48C57]" />
                <span className="text-white/60 text-xs uppercase tracking-[0.15em]">{t.monthlyEmi}</span>
              </div>
              <p className="font-serif text-3xl sm:text-4xl font-bold mt-2">
                <span className="text-[#D48C57]">&#8377;</span>{fmt(calculations.emi)}
                <span className="text-base font-normal text-white/50 ml-1">/{t.month}</span>
              </p>
            </div>

            {/* Breakdown cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-xl p-4 border border-[#E5DFD3]">
                <IndianRupee className="w-4 h-4 text-[#D48C57] mb-2" />
                <p className="text-[10px] text-[#5C6A61] uppercase tracking-wider">{t.totalCost}</p>
                <p className="font-serif text-lg font-bold text-[#1A2520] mt-0.5">&#8377;{fmt(calculations.totalCost)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E5DFD3]">
                <TrendingUp className="w-4 h-4 text-[#2C4C3B] mb-2" />
                <p className="text-[10px] text-[#5C6A61] uppercase tracking-wider">{t.downPayment}</p>
                <p className="font-serif text-lg font-bold text-[#1A2520] mt-0.5">&#8377;{fmt(calculations.downPayment)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E5DFD3]">
                <Clock className="w-4 h-4 text-[#5C6A61] mb-2" />
                <p className="text-[10px] text-[#5C6A61] uppercase tracking-wider">{t.loanAmount}</p>
                <p className="font-serif text-lg font-bold text-[#1A2520] mt-0.5">&#8377;{fmt(calculations.loanAmount)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E5DFD3]">
                <IndianRupee className="w-4 h-4 text-red-400 mb-2" />
                <p className="text-[10px] text-[#5C6A61] uppercase tracking-wider">{t.totalInterest}</p>
                <p className="font-serif text-lg font-bold text-[#1A2520] mt-0.5">&#8377;{fmt(calculations.totalInterest)}</p>
              </div>
            </div>

            {/* CTA */}
            <a
              href="tel:+919403890991"
              data-testid="emi-call-cta"
              className="block text-center bg-[#D48C57] hover:bg-[#C07A4A] text-white py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-[#D48C57]/20"
            >
              {t.callCta}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
